import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { storyblokEditable } from "@storyblok/react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { sbImage } from "../utils/storyblokImage";

// resolve links for buttons
const resolveStoryblokLink = (link) => {
  if (!link) return "#";

  if (link.linktype === "story") {
    const slug = link.cached_url || link.story?.full_slug;
    return slug ? `/${slug}` : "#";
  }

  if (link.linktype === "asset") {
    return link.url || "#";
  }

  return link.url || link.cached_url || "#";
};

// treat "/example" as internal with "//" and "http(s)://" external
const isInternalPath = (href) => {
  if (!href || typeof href !== "string") return false;
  const safe = href.trim();
  return safe.startsWith("/") && !safe.startsWith("//");
};

const SmartLink = ({ href, className, children, onClick, ...rest }) => {
  const safeHref = typeof href === "string" ? href.trim() : href;

  if (!safeHref || safeHref === "#") {
    return (
      <button type="button" className={className} onClick={onClick} {...rest}>
        {children}
      </button>
    );
  }

  if (isInternalPath(safeHref)) {
    return (
      <Link to={safeHref} className={className} onClick={onClick} {...rest}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={safeHref}
      className={className}
      onClick={onClick}
      rel={rest.target === "_blank" ? "noreferrer noopener" : undefined}
      {...rest}
    >
      {children}
    </a>
  );
};

const buildImageCandidates = (url, { w, h, q } = {}) => {
  if (!url) return [];
  const candidates = [
    sbImage(url, { w, h, q, fmt: "webp" }),
    sbImage(url, { w, h, q, fmt: "auto" }),
    url,
  ];

  return Array.from(new Set(candidates.filter(Boolean)));
};

const HeroSection = ({ blok }) => {
  const bgVideoRef = useRef(null);
  const overlayVideoRef = useRef(null);

  const [isActive, setIsActive] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // only controls media polish state now, not content visibility
  const [isBgReady, setIsBgReady] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const mediaSrc = blok?.media?.filename || "";
  const isVideo = useMemo(() => /\.(mp4|webm|ogg)$/i.test(mediaSrc), [mediaSrc]);

  const posterSrc = useMemo(() => {
    const p = blok?.poster?.filename || "";
    if (!p) return "";
    return sbImage(p, { w: 1920, h: 840, q: 80, fmt: "auto" });
  }, [blok?.poster?.filename]);

  const heroBgCandidates = useMemo(() => {
    if (!mediaSrc || isVideo) return [];
    return buildImageCandidates(mediaSrc, { w: 1920, h: 840, q: 80 });
  }, [mediaSrc, isVideo]);

  const [heroBgIndex, setHeroBgIndex] = useState(0);

  useEffect(() => {
    setHeroBgIndex(0);
    setIsBgReady(false);
    setIsVideoReady(false);
  }, [mediaSrc]);

  const heroBgImage = heroBgCandidates[heroBgIndex] || mediaSrc;

  const handleHeroBgError = useCallback(() => {
    setIsBgReady(false);
    setHeroBgIndex((i) => {
      const next = i + 1;
      return next < heroBgCandidates.length ? next : i;
    });
  }, [heroBgCandidates.length]);

  const primaryIconSrc = useMemo(() => {
    const u = blok?.primary_icon?.filename || "";
    if (!u) return "";
    return sbImage(u, { w: 38, h: 28, q: 90, fmt: "auto" });
  }, [blok?.primary_icon?.filename]);

  const secondaryIconSrc = useMemo(() => {
    const u = blok?.secondary_icon?.filename || "";
    if (!u) return "";
    return sbImage(u, { w: 20, h: 20, q: 90, fmt: "auto" });
  }, [blok?.secondary_icon?.filename]);

  // preload image background
  useEffect(() => {
    if (!mediaSrc) {
      setIsBgReady(true);
      return;
    }

    if (isVideo) return;

    if (!heroBgImage) {
      setIsBgReady(true);
      return;
    }

    let cancelled = false;
    setIsBgReady(false);

    const img = new Image();
    img.decoding = "async";
    img.src = heroBgImage;

    img.onload = () => {
      if (!cancelled) setIsBgReady(true);
    };

    img.onerror = () => {
      if (cancelled) return;
      handleHeroBgError();
    };

    return () => {
      cancelled = true;
    };
  }, [isVideo, mediaSrc, heroBgImage, handleHeroBgError]);

  // poster preload for video background
  useEffect(() => {
    if (!isVideo || !mediaSrc) return;

    if (!posterSrc) {
      setIsBgReady(true);
      return;
    }

    let cancelled = false;
    setIsBgReady(false);

    const img = new Image();
    img.decoding = "async";
    img.src = posterSrc;

    img.onload = () => {
      if (cancelled) return;
      setIsBgReady(true);
    };

    img.onerror = () => {
      if (cancelled) return;
      setIsBgReady(true);
    };

    return () => {
      cancelled = true;
    };
  }, [isVideo, mediaSrc, posterSrc]);

  useEffect(() => {
    if (!isVideo || !mediaSrc) return;
    const v = bgVideoRef.current;
    if (!v) return;

    try {
      v.load();
      const p = v.play?.();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch (_) {}
  }, [isVideo, mediaSrc]);

  useEffect(() => {
    if (!isVideo || !mediaSrc) return;
    const v = bgVideoRef.current;
    if (!v) return;

    setIsVideoReady(false);

    const markReady = () => setIsVideoReady(true);

    v.addEventListener("loadeddata", markReady);

    if (v.readyState >= 2) {
      setIsVideoReady(true);
    }

    return () => {
      v.removeEventListener("loadeddata", markReady);
    };
  }, [isVideo, mediaSrc]);

  // Lock scrolling when the mobile overlay is open
  useEffect(() => {
    if (!isOverlayOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOverlayOpen]);

  const closeOverlay = () => {
    const ov = overlayVideoRef.current;
    if (ov) {
      try {
        ov.pause();
        ov.currentTime = 0;
      } catch (_) {}
    }
    setIsOverlayOpen(false);
    setIsActive(false);
  };

  // close overlay when overlay video ends automatically
  useEffect(() => {
    if (!isOverlayOpen) return;
    const ov = overlayVideoRef.current;
    if (!ov) return;

    const handleEnded = () => closeOverlay();
    ov.addEventListener("ended", handleEnded);
    return () => ov.removeEventListener("ended", handleEnded);
  }, [isOverlayOpen]);

  // Video play
  const handlePlayClick = async () => {
    if (!isVideo) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    if (isMobile) {
      const bg = bgVideoRef.current;
      if (bg) {
        try {
          bg.pause();
        } catch (_) {}
      }

      setIsActive(true);
      setIsOverlayOpen(true);

      requestAnimationFrame(async () => {
        const ov = overlayVideoRef.current;
        if (!ov) return;

        try {
          ov.currentTime = 0;
          ov.muted = false;
          ov.controls = true;

          if (typeof ov.webkitEnterFullscreen === "function") {
            try {
              ov.webkitEnterFullscreen();
            } catch (_) {}
          }

          await ov.play();
        } catch (err) {
          console.warn("Overlay video play failed:", err);
        }
      });

      return;
    }

    const v = bgVideoRef.current;
    if (!v) return;

    try {
      if (v.requestFullscreen) {
        await v.requestFullscreen();
      } else if (v.webkitRequestFullscreen) {
        await v.webkitRequestFullscreen();
      } else if (v.msRequestFullscreen) {
        await v.msRequestFullscreen();
      }
    } catch (err) {
      console.warn("Fullscreen request failed:", err);
    }

    try {
      v.currentTime = 0;
      v.muted = false;
      v.controls = true;
      await v.play();
      setIsActive(true);
    } catch (err) {
      console.warn("Video play failed:", err);
    }
  };

  // Exit fullscreen automatically when desktop background video ends
  useEffect(() => {
    if (!isVideo || !mediaSrc) return;

    const v = bgVideoRef.current;
    if (!v) return;

    const handleEnded = () => {
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      }
      setIsActive(false);

      try {
        v.controls = false;
        v.muted = true;
        v.loop = true;
        v.play();
      } catch (_) {}
    };

    v.addEventListener("ended", handleEnded);
    return () => v.removeEventListener("ended", handleEnded);
  }, [isVideo, mediaSrc]);

  const primaryHref = resolveStoryblokLink(blok?.primary_button_link);
  const secondaryHref = resolveStoryblokLink(blok?.secondary_button_link);

  return (
    <section
      {...storyblokEditable(blok)}
      className="relative w-full h-[840px] overflow-hidden bg-black font-['Roboto']"
    >
      {/* Video preload with Helmet */}
      {isVideo && !!mediaSrc && (
        <Helmet>
          <link rel="preload" as="video" href={mediaSrc} type="video/mp4" />
        </Helmet>
      )}

      {/* Mobile video overlay */}
      {isVideo && isOverlayOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center pointer-events-auto">
          <button
            type="button"
            onClick={closeOverlay}
            aria-label="Close video"
            className="absolute top-4 right-4 z-[10000] text-white bg-white/10 hover:bg-white/20 rounded-full w-12 h-12 flex items-center justify-center"
          >
            <span className="text-2xl leading-none">×</span>
          </button>

          <video
            key={`overlay-${mediaSrc}`}
            ref={overlayVideoRef}
            src={mediaSrc}
            controls
            playsInline
            preload="auto"
            poster={posterSrc || blok?.poster?.filename}
            className="w-full h-full object-contain"
          />
        </div>
      )}

      {/* Background Media Item */}
      <div className="absolute inset-0 z-[20]">
        {isVideo ? (
          <div
            className={`relative w-full h-full transition-all duration-500 ${
              isBgReady && isVideoReady
                ? "blur-0 scale-100 opacity-100"
                : "blur-sm scale-[1.01] opacity-85"
            }`}
          >
            {/* Poster layer fades out once video has first frame */}
            {posterSrc && (
              <img
                src={posterSrc}
                alt=""
                aria-hidden="true"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                  isVideoReady ? "opacity-0" : "opacity-100"
                }`}
                decoding="async"
                fetchpriority="high"
              />
            )}

            {/* Video layer */}
            <video
              key={`bg-${mediaSrc}`}
              ref={bgVideoRef}
              src={mediaSrc}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster={posterSrc || blok?.poster?.filename}
              className={`object-cover w-full h-full transition-opacity duration-300 ${
                isVideoReady ? "opacity-100" : "opacity-0"
              }`}
            />

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent z-[25] pointer-events-none" />

            {/* Play button icon desktop */}
            {!isActive && (
              <button
                onClick={handlePlayClick}
                className="absolute inset-0 z-[40] hidden md:flex items-center justify-center pointer-events-auto cursor-pointer"
                aria-label="Play video with sound"
              >
                <div className="w-20 h-20 border-2 border-white rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="white"
                    viewBox="0 0 24 24"
                    className="w-8 h-8"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </button>
            )}
          </div>
        ) : (
          <div
            className={`relative w-full h-full transition-all duration-500 ${
              isBgReady
                ? "blur-0 scale-100 opacity-100"
                : "blur-sm scale-[1.01] opacity-85"
            }`}
          >
            {!!mediaSrc && (
              <img
                key={`img-${heroBgImage}`}
                src={heroBgImage}
                alt={blok?.media?.alt || "Hero background"}
                className="object-cover w-full h-full"
                decoding="async"
                fetchpriority="high"
                onError={handleHeroBgError}
              />
            )}

            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent z-[25] pointer-events-none" />
          </div>
        )}
      </div>

      {/* Content always visible immediately */}
      <div className="relative z-[50] flex items-center h-full px-10 md:px-20 text-white pointer-events-none">
        <div className="max-w-lg md:w-1/2 space-y-6">
          {/* Play button on mobile */}
          {isVideo && !isActive && (
            <div className="md:hidden mb-4 flex justify-center pointer-events-auto">
              <button
                onClick={handlePlayClick}
                className="inline-flex items-center justify-center w-16 h-16 border-2 border-white rounded-full bg-white/10 hover:bg-white/20 transition"
                aria-label="Play video with sound"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="white"
                  viewBox="0 0 24 24"
                  className="w-7 h-7"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            </div>
          )}

          {blok?.title && (
            <h1 className="text-3xl md:text-4xl font-medium leading-tight">
              {blok.title}
            </h1>
          )}

          {blok?.subheading && (
            <h2 className="text-[20px] font-normal text-white">
              {blok.subheading}
            </h2>
          )}

          {blok?.paragraph && (
            <p className="text-[16px] font-normal text-white">
              {blok.paragraph}
            </p>
          )}
          
{/* Buttons */}
<div className="flex flex-nowrap items-center gap-6 pointer-events-auto">
  {blok?.primary_button && (
    <SmartLink
      href={primaryHref}
      className="flex shrink-0 items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-full font-medium text-sm transition whitespace-nowrap"
    >
      {blok?.primary_icon?.filename && (
        <img
          src={primaryIconSrc || blok.primary_icon.filename}
          alt={blok.primary_icon.alt || ""}
          width={19}
          height={14}
          decoding="async"
        />
      )}

      {blok.primary_button}
    </SmartLink>
  )}

  {blok?.secondary_button && (
    <SmartLink
      href={secondaryHref}
      className="flex shrink-0 items-center gap-2 text-white hover:underline font-medium text-sm whitespace-nowrap"
    >
      {blok.secondary_button}

      {blok?.secondary_icon?.filename && (
        <img
          src={secondaryIconSrc || blok.secondary_icon.filename}
          alt={blok.secondary_icon.alt || ""}
          width={10}
          height={10}
          className="inline-block"
          decoding="async"
        />
      )}
    </SmartLink>
  )}
</div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;